import { FC, ReactNode, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ThemeSwitcher from '../components/ThemeSwitcher';
import LanguageSwitcher from '../components/LanguageSwitcher';
import StoreBadges from '../components/StoreBadges';
import { localizeSiteModeMessage, useSiteMode } from '../hooks/useSiteMode';
import { useLanguage } from '../i18n/useLanguage';
import type { Language } from '../i18n/language';
import { legalContact } from '../content/legalContent';

const logoSizeClass = {
  header: "flex h-12 shrink-0 items-center gap-2 min-[420px]:h-14 min-[420px]:gap-3 md:h-16",
  footer: "flex h-16 shrink-0 items-center gap-3 sm:h-[72px]",
};

// Logo bileşeni
const Logo = ({ variant = 'footer' }: { variant?: 'header' | 'footer' }) => (
  <Link
    to="/"
    aria-label="Foliano: English by Levels"
    className={logoSizeClass[variant]}
  >
    <img
      src="/images/foliano-mascot.png?v=1"
      alt=""
      className="h-full w-auto object-contain"
    />
    <span className="hidden flex-col leading-none min-[420px]:flex">
      <span className="text-xl font-extrabold tracking-tight text-primary-600 dark:text-primary-400">Foliano</span>
      <span className="mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-200">English by Levels</span>
    </span>
  </Link>
);

// Ana düzen bileşeni
interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout: FC<MainLayoutProps> = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { language } = useLanguage();
  
  // Sayfada aşağı kaydırma durumunda navigasyon çubuğunu değiştir
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const sectionId = decodeURIComponent(location.hash.replace('#', ''));
      let retryTimer: number | null = null;
      let attempts = 0;
      let cancelled = false;
      const scrollWhenReady = () => {
        if (cancelled) return;
        const target = document.getElementById(sectionId);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        attempts += 1;
        if (attempts < 100) {
          retryTimer = window.setTimeout(scrollWhenReady, 50);
        }
      };

      scrollWhenReady();
      return () => {
        cancelled = true;
        if (retryTimer !== null) window.clearTimeout(retryTimer);
      };
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    return undefined;
  }, [location.pathname, location.hash]);
  
  const copy: Record<Language, {
    menu: string[];
    menuButtonLabel: string;
    demoLabel: string;
    footerDescription: string;
    pages: string;
    legal: string;
    contact: string;
    support: string;
    rights: string;
    legalLinks: string[];
  }> = {
    tr: {
      menu: ['Özellikler', 'Nimonik', 'Motivasyon', 'Ekranlar', 'Yenilikler'],
      menuButtonLabel: 'Menüyü aç',
      demoLabel: 'Demo modundasınız. Etkileşimli aksiyonlar devre dışı.',
      footerDescription: 'Foliano: English by Levels, seviyeye göre İngilizce öğrenme ve pratik için tasarlanmış mobil uygulama.',
      pages: 'Sayfalar',
      legal: 'Yasal',
      contact: 'İletişim',
      support: 'Destek',
      rights: 'Tüm hakları saklıdır.',
      legalLinks: ['Gizlilik Politikası', 'Kullanım Koşulları', 'Hesap Silme', 'Destek'],
    },
    en: {
      menu: ['Features', 'Mnemonic', 'Motivation', 'Screens', 'What\'s New'],
      menuButtonLabel: 'Open menu',
      demoLabel: 'You are in demo mode. Interactive actions are disabled.',
      footerDescription: 'Foliano: English by Levels is a mobile app designed for level-based English learning and practice.',
      pages: 'Pages',
      legal: 'Legal',
      contact: 'Contact',
      support: 'Support',
      rights: 'All rights reserved.',
      legalLinks: ['Privacy Policy', 'Terms of Use', 'Account Deletion', 'Support'],
    },
  };
  const t = copy[language];
  const menuAnchors = ['features', 'mnemonic', 'motivation', 'screens', 'whats-new'];
  const menuItems = t.menu.map((name, index) => ({ name, to: `/#${menuAnchors[index]}` }));
  const legalTargets = ['/privacy', '/terms', '/account-deletion', '/support'];
  const legalLinks = t.legalLinks.map((name, index) => ({ name, to: legalTargets[index] }));

  const { isDemo, message } = useSiteMode();
  const localizedSiteModeMessage = localizeSiteModeMessage(message, language);

  return (
    <div className="flex min-h-screen flex-col">
      {/* Header */}
      <header
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          isScrolled
            ? "bg-white/[0.88] shadow-sm backdrop-blur-md dark:bg-slate-900/90"
            : "bg-[#EEE8F9]/[0.82] backdrop-blur-sm dark:bg-slate-950/70"
        }`}
      >
        <div className="container-custom">
          <div className="flex h-20 items-center justify-between">
            {/* Logo */}
            <Logo variant="header" />

            {/* Sağ üst: Masaüstü menü + dil + tema switcher */}
            <div className="flex items-center">
              {/* Masaüstü menü */}
              <div className="hidden lg:block">
                <ul className="flex items-center gap-5 xl:gap-8">
                  {menuItems.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.to}
                        className="whitespace-nowrap font-semibold text-slate-700 hover:text-primary-600 dark:text-gray-200 dark:hover:text-primary-400"
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                  {/* Seviye filtreleme dropdown'ı ileride eklenebilir */}
                </ul>
              </div>
              <LanguageSwitcher />
              {/* Tema switcher */}
              <ThemeSwitcher />
            </div>

            {/* Mobil menü butonu */}
            <button
              type="button"
              className="ml-1 inline-flex h-10 w-10 items-center justify-center rounded-full text-sm text-slate-500 transition hover:bg-white/70 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/25 lg:hidden dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-primary-400"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-controls="mobile-navigation"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">{t.menuButtonLabel}</span>
              <svg
                className="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                {isMobileMenuOpen ? (
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  ></path>
                ) : (
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  ></path>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobil menü */}
        <div
          id="mobile-navigation"
          className={`overflow-hidden border-t border-white/60 bg-[#F6F1FF]/95 shadow-[0_18px_40px_rgba(15,23,42,0.10)] backdrop-blur-xl transition-[max-height,opacity] duration-200 lg:hidden dark:border-white/10 dark:bg-slate-950/95 ${
            isMobileMenuOpen ? "max-h-80 opacity-100" : "pointer-events-none max-h-0 opacity-0"
          }`}
        >
          <nav className="container-custom py-2" aria-label="Mobile navigation">
            <ul className="divide-y divide-slate-200/70 dark:divide-white/10">
              {menuItems.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.to}
                    className="flex h-11 items-center rounded-md px-3 text-sm font-semibold text-slate-700 transition hover:bg-white/75 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/20 dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-primary-400"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </header>

      {isDemo && (
        <div className="bg-[#FFF3D7]/[0.85] py-2 text-center text-sm font-semibold text-[#B45309] dark:bg-amber-950/40 dark:text-amber-200">
          {t.demoLabel}{localizedSiteModeMessage ? ` ${localizedSiteModeMessage}` : ''}
        </div>
      )}

      {/* Ana içerik */}
      <main className="flex-grow">{children}</main>

      {/* Altbilgi */}
      <footer className="mt-auto bg-gray-100 py-12 dark:bg-gray-800">
        <div className="container-custom max-w-6xl mx-auto">
          <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-[1.15fr_0.8fr_0.85fr_1.25fr]">
            <div className="min-w-0 max-w-sm">
              <Logo />
              <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                {t.footerDescription}
              </p>
            </div>

            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-200">
                {t.pages}
              </h3>
              <ul className="space-y-2 text-sm">
                {menuItems.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.to}
                      className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-200">
                {t.legal}
              </h3>
              <ul className="space-y-2 text-sm">
                {legalLinks.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.to}
                      className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="min-w-0">
              <h3 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-200">
                {t.contact}
              </h3>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li>
                  {t.support}:{' '}
                  <a
                    href={`mailto:${legalContact.supportEmail}`}
                    className="break-words text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
                  >
                    {legalContact.supportEmail}
                  </a>
                </li>
              </ul>
              <StoreBadges language={language} className="mt-5" />
            </div>
          </div>

          <div className="mt-8 border-t border-gray-200 pt-6 dark:border-gray-700">
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              &copy; {new Date().getFullYear()} Foliano: English by Levels. {t.rights}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLayout; 
